import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { fmt } from '../utils/api';
import useCollapsible from '../utils/useCollapsible';

const OVER_COLOR = '#ef4444';
const UNDER_COLOR = '#00d4aa';
const FUTURE_COLOR = '#374151';
const ESTIMATED_COLOR = '#fbbf24';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const { total, over, diff, budget, isFuture, isEstimated, isPartiallyEstimated, bigPurchaseAmount } = payload[0].payload;
    if (isFuture) {
      return (
        <div style={styles.tooltip}>
          <div style={styles.tooltipLabel}>{label}</div>
          <div style={{ ...styles.tooltipValue, color: '#6b7280' }}>
            Projected budget: {fmt.currency(budget)}
          </div>
          <div style={styles.tooltipDiff}>Hasn't happened yet</div>
        </div>
      );
    }
    if (isEstimated) {
      return (
        <div style={styles.tooltip}>
          <div style={styles.tooltipLabel}>{label}</div>
          <div style={{ ...styles.tooltipValue, color: '#fbbf24' }}>
            ~{fmt.currency(total)}
          </div>
          <div style={{ ...styles.tooltipDiff, color: '#fbbf24' }}>
            Estimated - no transaction history synced for this month, based on your average from tracked months
          </div>
        </div>
      );
    }
    if (isPartiallyEstimated) {
      return (
        <div style={styles.tooltip}>
          <div style={styles.tooltipLabel}>{label}</div>
          <div style={{ ...styles.tooltipValue, color: over ? OVER_COLOR : UNDER_COLOR }}>
            {fmt.currency(total)}
          </div>
          <div style={{ ...styles.tooltipDiff, color: '#fbbf24' }}>
            Includes an estimate for the early days of the month, before transaction syncing started
          </div>
          {bigPurchaseAmount > 0 && (
            <div style={{ ...styles.tooltipDiff, color: '#ef4444' }}>
              💥 Includes {fmt.currency(bigPurchaseAmount)} in big purchases
            </div>
          )}
        </div>
      );
    }
    return (
      <div style={styles.tooltip}>
        <div style={styles.tooltipLabel}>{label}</div>
        <div style={{ ...styles.tooltipValue, color: over ? OVER_COLOR : UNDER_COLOR }}>
          {fmt.currency(total)}
        </div>
        <div style={styles.tooltipDiff}>
          {over ? 'Over by ' : 'Under by '}
          {fmt.currency(Math.abs(diff))}
          {' · budget was '}{fmt.currency(budget)}
        </div>
        {bigPurchaseAmount > 0 && (
          <div style={{ ...styles.tooltipDiff, color: '#ef4444' }}>
            💥 Includes {fmt.currency(bigPurchaseAmount)} in big purchases
          </div>
        )}
      </div>
    );
  }
  return null;
};

export default function SpendingChart({ monthlySpend }) {
  const [collapsed, toggleCollapsed] = useCollapsible('spendingChart');
  if (!monthlySpend || monthlySpend.length === 0) return null;

  const data = monthlySpend.map((row) => {
    const total = row.hasData ? parseFloat(row.total) : null;
    const budget = parseFloat(row.budget);
    return {
      month: row.month.slice(5),
      total,
      budget,
      isFuture: row.isFuture,
      isCurrent: row.isCurrent,
      isEstimated: !!row.isEstimated,
      isPartiallyEstimated: !!row.isPartiallyEstimated,
      bigPurchaseAmount: row.bigPurchaseAmount || 0,
      over: total != null ? total - budget > 0.01 : false,
      diff: total != null ? total - budget : 0,
      label: new Date(`${row.month}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short' }),
      // For future months, render the projected budget itself as a translucent bar
      // so there's still something visible on the chart at that month's position
      displayValue: total != null ? total : budget,
    };
  });

  const completedMonths = data.filter((d) => !d.isFuture && !d.isEstimated);
  const overCount = completedMonths.filter((d) => d.over).length;
  const underCount = completedMonths.length - overCount;
  const estimatedCount = data.filter((d) => d.isEstimated).length;
  const currentOrLatest = [...data].reverse().find((d) => !d.isFuture) || data[0];

  return (
    <div style={styles.chart}>
      <div style={styles.header}>
        <button style={styles.collapseBtn} onClick={toggleCollapsed}>
          <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
          <h2 style={styles.title}>📊 Monthly Snapshot</h2>
        </button>
        <span style={styles.budget}>
          {currentOrLatest.isCurrent ? 'This month' : 'Latest'} budget: {fmt.currency(currentOrLatest.budget)}/mo
        </span>
      </div>

      {!collapsed && (
      <>
      <div style={styles.summaryRow}>
        <span style={{ ...styles.summaryPill, color: UNDER_COLOR, borderColor: UNDER_COLOR }}>
          {underCount} under budget
        </span>
        <span style={{ ...styles.summaryPill, color: OVER_COLOR, borderColor: OVER_COLOR }}>
          {overCount} over budget
        </span>
        {estimatedCount > 0 && (
          <span style={{ ...styles.summaryPill, color: ESTIMATED_COLOR, borderColor: ESTIMATED_COLOR }}>
            ~{estimatedCount} estimated
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
          <XAxis
            dataKey="label"
            tick={{ fill: '#6b7280', fontSize: 11, fontFamily: "'DM Mono', monospace" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
          <Bar dataKey="displayValue" radius={[4, 4, 0, 0]} maxBarSize={32}>
            {data.map((entry, index) => (
              <Cell
                key={index}
                fill={entry.isFuture ? FUTURE_COLOR : (entry.isEstimated ? ESTIMATED_COLOR : (entry.over ? OVER_COLOR : UNDER_COLOR))}
                fillOpacity={entry.isFuture ? 0.35 : (entry.isEstimated ? 0.5 : 1)}
                stroke={(entry.isEstimated || entry.isPartiallyEstimated) ? ESTIMATED_COLOR : 'none'}
                strokeWidth={(entry.isEstimated || entry.isPartiallyEstimated) ? 1 : 0}
                strokeDasharray={entry.isEstimated ? '3 3' : (entry.isPartiallyEstimated ? '2 2' : '0')}
              />
            ))}
            <LabelList
              dataKey="bigPurchaseAmount"
              content={(props) => {
                const { x, y, width, value, index } = props;
                if (!value || value <= 0) return null;
                return (
                  <text x={x + width / 2} y={y - 8} textAnchor="middle" fontSize="11" style={{ pointerEvents: 'none' }}>
                    💥
                  </text>
                );
              }}
            />
          </Bar>
          <Line
            dataKey="budget"
            stroke="#fbbf24"
            strokeWidth={1.5}
            strokeDasharray="4 4"
            dot={false}
            type="stepAfter"
          />
        </ComposedChart>
      </ResponsiveContainer>

      <div style={styles.legend}>
        <span style={styles.legendItem}>
          <span style={{ ...styles.dot, background: UNDER_COLOR }} /> Under budget
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.dot, background: OVER_COLOR }} /> Over budget
        </span>
        <span style={styles.legendItem}>
          <span style={{ ...styles.dot, background: FUTURE_COLOR, opacity: 0.5 }} /> Projected (hasn't happened)
        </span>
        <span style={styles.legendItem}>
          <span style={styles.dash} /> Budget line
        </span>
        <span style={styles.legendItem}>
          💥 Includes a big purchase
        </span>
      </div>
      </>
      )}
    </div>
  );
}

const styles = {
  chart: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  collapseBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  chevron: {
    color: '#6b7280',
    fontSize: '0.8rem',
    width: '0.8rem',
  },
  budget: {
    color: '#6b7280',
    fontSize: '0.75rem',
    fontFamily: "'DM Mono', monospace",
  },
  summaryRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  summaryPill: {
    border: '1px solid',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '0.7rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  tooltip: {
    background: '#1e2a3a',
    border: '1px solid #374151',
    borderRadius: '8px',
    padding: '8px 12px',
  },
  tooltipLabel: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
  tooltipValue: {
    fontSize: '1rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  tooltipDiff: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
    marginTop: '2px',
  },
  legend: {
    display: 'flex',
    gap: '1rem',
    marginTop: '0.75rem',
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'Syne', sans-serif",
  },
  dot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  dash: {
    display: 'inline-block',
    width: 14,
    height: 2,
    background: '#fbbf24',
    borderStyle: 'dashed',
  },
};
